import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChatCircleDots, ImageSquare, PencilLine, Sparkle } from '@phosphor-icons/react';

/**
 * ORACLE RESEARCH LAB
 * Deep AI Tools Integration
 * 
 * Three embedded DeepAI tools:
 * - AI Chat
 * - Image Generator  
 * - Image Editor
 * 
 * Only active tab's iframe is rendered (performance optimization)
 */

const ResearchLab = () => {
  const [activeTab, setActiveTab] = useState('chat');

  const tabs = [
    {
      id: 'chat',
      label: 'AI Chat',
      icon: ChatCircleDots,
      url: 'https://deepai.org/chat',
      description: 'Conversational AI powered by advanced language models'
    },
    {
      id: 'generator',
      label: 'Image Generator',
      icon: ImageSquare,
      url: 'https://deepai.org/machine-learning-model/text2img',
      description: 'Transform text descriptions into stunning visual art'
    },
    {
      id: 'editor',
      label: 'Image Editor',
      icon: PencilLine,
      url: 'https://deepai.org/machine-learning-model/image-editor',
      description: 'AI-powered image manipulation and enhancement'
    }
  ];

  const activeTabData = tabs.find(tab => tab.id === activeTab);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A0A0F] via-black to-[#0A0A0F] pt-24 pb-16 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FFB800]/20 border border-[#FFB800]/50 rounded-full mb-4">
            <Sparkle size={16} weight="fill" className="text-[#FFB800]" />
            <span className="text-xs font-bold text-[#FFB800] mono tracking-wider">
              ORACLE RESEARCH LAB
            </span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-black text-white mb-4">
            Deep AI Tools
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Advanced AI capabilities at your fingertips. Chat, create, and edit with state-of-the-art models.
          </p>
        </motion.div>

        {/* Tab Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex flex-wrap justify-center gap-4 mb-8"
        >
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  relative px-8 py-4 rounded-xl font-bold text-lg transition-all
                  flex items-center gap-3 min-w-[200px] justify-center
                  ${isActive 
                    ? 'bg-gradient-to-r from-[#FFB800] to-[#FFA600] text-black shadow-2xl scale-105' 
                    : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/10'
                  }
                `}
              >
                {isActive && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute inset-0 bg-gradient-to-r from-[#FFB800] to-[#FFA600] rounded-xl"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                  />
                )}
                <Icon size={24} weight="fill" className="relative z-10" />
                <span className="relative z-10">{tab.label}</span>
              </button>
            );
          })}
        </motion.div>

        {/* Tab Description */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="text-center mb-8"
          >
            <p className="text-gray-400 text-sm max-w-2xl mx-auto">
              {activeTabData?.description}
            </p>
          </motion.div>
        </AnimatePresence>

        {/* Iframe Container */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
          className="relative bg-black/40 backdrop-blur-sm border-2 border-[#FFB800]/30 rounded-2xl p-4 shadow-2xl overflow-hidden"
          style={{
            boxShadow: '0 0 40px rgba(255, 184, 0, 0.2), inset 0 0 60px rgba(255, 184, 0, 0.05)'
          }}
        >
          {/* Decorative corners */}
          <div className="absolute top-2 left-2 w-6 h-6 border-t-2 border-l-2 border-[#FFB800]/50"></div>
          <div className="absolute top-2 right-2 w-6 h-6 border-t-2 border-r-2 border-[#FFB800]/50"></div>
          <div className="absolute bottom-2 left-2 w-6 h-6 border-b-2 border-l-2 border-[#FFB800]/50"></div>
          <div className="absolute bottom-2 right-2 w-6 h-6 border-b-2 border-r-2 border-[#FFB800]/50"></div>

          {/* Only render the active tab's iframe */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <iframe
                src={activeTabData?.url}
                width="100%"
                height="700"
                style={{
                  border: 'none',
                  borderRadius: '12px',
                  backgroundColor: '#ffffff'
                }}
                loading="lazy"
                title={`Deep AI - ${activeTabData?.label}`}
                allow="clipboard-write"
                className="w-full"
              />
            </motion.div>
          </AnimatePresence>
        </motion.div>

        {/* Info Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-8 p-6 bg-[#FFB800]/10 border border-[#FFB800]/30 rounded-xl"
        >
          <div className="flex items-start gap-4">
            <Sparkle size={24} weight="fill" className="text-[#FFB800] flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-white font-bold mb-2">Powered by DeepAI</h3>
              <p className="text-sm text-gray-400 leading-relaxed">
                These tools are provided by DeepAI.org, a leading platform for accessible AI technology. 
                All processing happens on DeepAI's servers. Your interactions are subject to DeepAI's terms of service and privacy policy.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Quick Actions */}
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="p-6 glass-card border border-white/10 rounded-xl text-center hover:border-[#FFB800]/50 transition-all"
          >
            <div className="w-12 h-12 bg-[#00F0FF]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <ChatCircleDots size={24} weight="fill" className="text-[#00F0FF]" />
            </div>
            <h4 className="text-white font-bold mb-2">Ask Anything</h4>
            <p className="text-sm text-gray-400">
              Get instant answers, generate code, analyze data, and more with AI Chat
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="p-6 glass-card border border-white/10 rounded-xl text-center hover:border-[#FFB800]/50 transition-all"
          >
            <div className="w-12 h-12 bg-[#FF00FF]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <ImageSquare size={24} weight="fill" className="text-[#FF00FF]" />
            </div>
            <h4 className="text-white font-bold mb-2">Create Visuals</h4>
            <p className="text-sm text-gray-400">
              Transform ideas into images with text-to-image AI generation
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="p-6 glass-card border border-white/10 rounded-xl text-center hover:border-[#FFB800]/50 transition-all"
          >
            <div className="w-12 h-12 bg-[#00FFA3]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <PencilLine size={24} weight="fill" className="text-[#00FFA3]" />
            </div>
            <h4 className="text-white font-bold mb-2">Edit Images</h4>
            <p className="text-sm text-gray-400">
              Enhance, modify, and perfect your images with AI-powered editing
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ResearchLab;
